import { create } from 'zustand'
import api, { DataAssetData, AssetChunkData } from '../services/api'

type StatusFilter = 'all' | 'none' | 'pending' | 'indexing' | 'indexed' | 'failed'

interface DatasourceState {
  assets: DataAssetData[]
  chunks: AssetChunkData[]
  selectedAssetId: string | null
  loading: boolean
  uploading: boolean
  error: string | null
  statusFilter: StatusFilter
  usageBytes: number
  usageCount: number

  fetchAssets: (projectId: string) => Promise<void>
  fetchChunks: (assetId: string) => Promise<void>
  uploadFile: (projectId: string, file: File, parseStrategy?: string, storageMode?: string) => Promise<DataAssetData>
  deleteAsset: (assetId: string, projectId: string) => Promise<void>
  shareAsset: (assetId: string, visibility: string, sharedWithId?: string) => Promise<void>
  unshareAsset: (assetId: string) => Promise<void>
  reparseAsset: (assetId: string) => Promise<void>
  linkChunkToKnowledge: (assetId: string, chunkId: string, knowledgeNodeId: string) => Promise<void>
  fetchUsage: () => Promise<void>
  selectAsset: (assetId: string | null) => void
  setStatusFilter: (filter: StatusFilter) => void
  clear: () => void
}

let identityGeneration = 0

export const useDatasourceStore = create<DatasourceState>()((set, get) => ({
  assets: [],
  chunks: [],
  selectedAssetId: null,
  loading: false,
  uploading: false,
  error: null,
  statusFilter: 'all',
  usageBytes: 0,
  usageCount: 0,

  fetchAssets: async (projectId: string) => {
    const generation = identityGeneration
    set({ loading: true, error: null })
    try {
      const { statusFilter } = get()
      const statusArg = statusFilter === 'all' ? undefined : statusFilter
      const { assets } = await api.listDataAssets(projectId, statusArg)
      if (generation !== identityGeneration) return
      set({ assets, loading: false })
    } catch (err) {
      if (generation !== identityGeneration) return
      set({
        error: err instanceof Error ? err.message : 'Failed to load assets',
        loading: false,
      })
    }
  },

  fetchChunks: async (assetId: string) => {
    const generation = identityGeneration
    try {
      const { chunks } = await api.listAssetChunks(assetId)
      if (generation !== identityGeneration) return
      set({ chunks })
    } catch {
      if (generation !== identityGeneration) return
      set({ chunks: [] })
    }
  },

  uploadFile: async (projectId: string, file: File, parseStrategy?: string, storageMode?: string) => {
    const generation = identityGeneration
    set({ uploading: true, error: null })
    try {
      const { asset } = await api.uploadFile(projectId, file, parseStrategy, storageMode)
      if (generation !== identityGeneration) throw new Error('Account changed')
      set(state => ({
        assets: [asset, ...state.assets],
        uploading: false,
      }))
      get().fetchUsage()
      return asset
    } catch (err) {
      if (generation !== identityGeneration) throw err
      set({
        error: err instanceof Error ? err.message : 'Upload failed',
        uploading: false,
      })
      throw err
    }
  },

  deleteAsset: async (assetId: string, _projectId: string) => {
    const generation = identityGeneration
    try {
      await api.deleteDataAsset(assetId)
      if (generation !== identityGeneration) return
      set(state => ({
        assets: state.assets.filter(a => a.asset_id !== assetId),
        selectedAssetId: state.selectedAssetId === assetId ? null : state.selectedAssetId,
        chunks: state.selectedAssetId === assetId ? [] : state.chunks,
      }))
      get().fetchUsage()
    } catch (err) {
      if (generation !== identityGeneration) return
      set({ error: err instanceof Error ? err.message : 'Failed to delete' })
    }
  },

  shareAsset: async (assetId: string, visibility: string, sharedWithId?: string) => {
    const generation = identityGeneration
    try {
      await api.shareDataAsset(assetId, visibility, sharedWithId)
      if (generation !== identityGeneration) return
      set(state => ({
        assets: state.assets.map(a =>
          a.asset_id === assetId
            ? { ...a, visibility, shared_with_id: sharedWithId || null }
            : a,
        ),
      }))
    } catch (err) {
      if (generation !== identityGeneration) return
      set({ error: err instanceof Error ? err.message : 'Failed to share' })
    }
  },

  unshareAsset: async (assetId: string) => {
    const generation = identityGeneration
    try {
      await api.unshareDataAsset(assetId)
      if (generation !== identityGeneration) return
      set(state => ({
        assets: state.assets.map(a =>
          a.asset_id === assetId
            ? { ...a, visibility: 'personal', shared_with_id: null }
            : a,
        ),
      }))
    } catch (err) {
      if (generation !== identityGeneration) return
      set({ error: err instanceof Error ? err.message : 'Failed to unshare' })
    }
  },

  reparseAsset: async (assetId: string) => {
    const generation = identityGeneration
    try {
      await api.reparseAsset(assetId)
      if (generation !== identityGeneration) return
      set(state => ({
        assets: state.assets.map(a =>
          a.asset_id === assetId ? { ...a, index_status: 'indexing' as const } : a,
        ),
      }))
    } catch (err) {
      if (generation !== identityGeneration) return
      set({ error: err instanceof Error ? err.message : 'Failed to reparse' })
    }
  },

  linkChunkToKnowledge: async (assetId: string, chunkId: string, knowledgeNodeId: string) => {
    const generation = identityGeneration
    try {
      await api.linkChunkToKnowledge(assetId, chunkId, knowledgeNodeId)
      if (generation !== identityGeneration) return
      set(state => ({
        chunks: state.chunks.map(c =>
          c.chunk_id === chunkId
            ? { ...c, linked_knowledge_node_id: knowledgeNodeId }
            : c,
        ),
      }))
    } catch (err) {
      if (generation !== identityGeneration) return
      set({ error: err instanceof Error ? err.message : 'Failed to link chunk' })
    }
  },

  fetchUsage: async () => {
    const generation = identityGeneration
    try {
      const { total_bytes, asset_count } = await api.getStorageUsage()
      if (generation !== identityGeneration) return
      set({ usageBytes: total_bytes, usageCount: asset_count })
    } catch {
      if (generation !== identityGeneration) return
      // Non-critical
    }
  },

  selectAsset: (assetId) => {
    set({ selectedAssetId: assetId, chunks: [] })
    if (assetId) get().fetchChunks(assetId)
  },

  setStatusFilter: (filter) => set({ statusFilter: filter }),
  clear: () => {
    identityGeneration += 1
    set({ assets: [], chunks: [], selectedAssetId: null, error: null, usageBytes: 0, usageCount: 0, loading: false, uploading: false, statusFilter: 'all' })
  },
}))
